/* global NodeJS, setTimeout, clearTimeout */

import React, { Dispatch, ReactElement, SetStateAction, useEffect, useState } from "react";
import { IDocumentPartProps } from "./IDocumentPartProps";

import {
  ItemRenderProps,
  processTreeViewItems,
  TreeView,
  TreeViewExpandChangeEvent,
  TreeViewItemClickEvent,
} from "@progress/kendo-react-treeview";
import "@progress/kendo-theme-bootstrap/dist/all.css";
import { fetchActionWorkflow } from "../../api/document/DocumentsPart";
import {
  ContextualMenuItemType,
  IContextualMenuItem,
  IContextualMenuProps,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { DocumentRoot } from "../../api/models/document-root";
import { DocumentPart } from "../../api/models";
import { IconButton } from "@fluentui/react/lib/Button";
import { WorkflowStatus } from "../../api/models/workflow-status";
import { DocumentPartWithStatus } from "../../api/models/document-part-with-status";
import { fetchExpandedNodeExternal } from "./DocumentPartTreeHelper";
import { generateIdentifier } from "../../utils/helpers/RobotFramework";
import { useWindowSize } from "../../utils/helpers/useWindowSizeHook";
import { fetchDocumentPath } from "../../api/document/Documents";
import { DEFAULT_FOLDER_CREATION } from "../document/WithApiFileLocationProvider";

const clampNumber = (num, a, b) => Math.max(Math.min(num, Math.max(a, b)), Math.min(a, b));

export const Tooltip = ({
  positionY,
  children,
}: {
  positionY: number;
  positionX: number;
  children: ReactElement | string | (ReactElement | string)[];
}) => {
  const { width, height } = useWindowSize();

  const positionMinusY = height - positionY;
  const bottomOffset = clampNumber(positionMinusY + 30, 5, height - 5);
  const topOffset = clampNumber(positionY + 30, 5, height - 5);
  const isInBottonScreen = positionY > height / 2;
  const topBottomStyles = isInBottonScreen ? { bottom: bottomOffset } : { top: topOffset };

  return (
    <div
      className="tooltip"
      style={{
        position: "fixed",
        backgroundColor: "lightgray",
        color: "black",
        borderRadius: "5px",
        zIndex: 1000000000,
        opacity: 1,
        maxWidth: width - 10 + "px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        right: "50%",
        transform: "translateX(50%)",
        ...topBottomStyles,
      }}
    >
      {children}
    </div>
  );
};

export interface DocumentPartTreeSelection {
  item: any;
  IdRoot: string;
  name: string;
  key: string;
  order: number;
  oidPath: string[];
  data: any;
}

export interface TreeState {
  rootId: string;
  treeItems: TreeViewDataItem[];
  selectState: string[];
  selectedId?: string;
  page: string;
}

export interface TreeViewDataItem {
  text: string;
  key: string;
  order: number;
  hasChildren: boolean;
  nodeType: "part" | "section" | "root" | "pagination";
  expanded?: boolean;
  checked?: boolean;
  selected?: boolean;
  items?: TreeViewDataItem[];
  data: any;
  driveItemId?: string;
  hasReadPermissions?: boolean;
  fatherMultilinked?: boolean;
  fatherKey?: string;
  multilinkedParents?: DocumentPart[];
}

/**
 * Ultis functions
 */
export function mapFormToDataItem(part: DocumentRoot): TreeViewDataItem {
  return {
    text: part.documentPart.name.replace(/\.docx$/, ""),
    key: part.documentPart.id,
    order: part.documentPart.order,
    hasChildren: part.documentPart.hasChildren,
    nodeType: "part",
    expanded: false,
    items: undefined,
    data: part,
    hasReadPermissions: part.hasReadPermissions,
  };
}

function iconClassName(item: TreeViewDataItem) {
  if (item.nodeType == "pagination") return;
  if (item.nodeType == "section") return "k-icon k-i-excel";
  return "k-icon k-i-word";
}

function checkSignificantStatus(item: WorkflowStatus) {
  return !(item?.isSignificant || item?.significant);
}

function iconCertification(item: TreeViewDataItem, documentPartWithStatus: DocumentPartWithStatus[]) {
  const itemState = getWorkflowStatus(item.key, documentPartWithStatus);
  if (itemState == null) {
    return "loading_status";
  }
  if (checkSignificantStatus(itemState)) {
    return "ms-significant_icon";
  }
  switch (itemState?.workflowStatusCode) {
    case "20":
      return "ms-in_progress_icon";
    case "30":
      // eslint-disable-next-line no-case-declarations
      const element: string[] = itemState.actions;
      if (element?.includes("APPROVE")) {
        return "ms-pending_withpermission_icon";
      } else {
        return "ms-pending_withoutpermission_icon";
      }
    case "40":
      return "ms-rejection_icon";
    case "50":
      return "ms-submission_icon";
    default:
      return "ms-initial_state_icon";
  }
}

const RenderMenuIcon = () => <span />;

export const TreeItemRow = (props: {
  item: TreeViewDataItem;
  itemHierarchicalIndex: string;
  isSelected: boolean;
  isDraggable?: boolean;
  getIconButtonMenuProps?: (
    item: any,
    itemHierarchicalIndex: string,
    documentPartWhithStatus: DocumentPartWithStatus[]
  ) => IContextualMenuProps;
  onClick?: React.MouseEventHandler;
  hasReadPermissions?: boolean;
  onChangePage?: (key: string, page: string) => React.MouseEventHandler;
  fetchExpandedNode?: (id: string, isReload: boolean, page: number) => void;
  isLinkExisting?: boolean;
  certificationOptions?: (item: TreeViewDataItem) => IContextualMenuProps;
  isOutlineRibbon?: boolean;
  isOpenOutlineRibbon?: boolean;
  azureDefaultSite?: string;
  setSelectedItem?: (item: TreeViewDataItem) => void;
  elementToUpdate?: Dispatch<SetStateAction<DocumentRoot[]>>;
  documentPartWhithStatus?: DocumentPartWithStatus[];
  setDocumentPartWhithStatus?: (documentPartWhithStatus: DocumentPartWithStatus) => void;
}) => {
  const multiLinked = props.item.data.multiLinked ?? props.item.data.documentPart?.multiLinked;
  const menuProps =
    props.item.hasReadPermissions &&
    !props.isDraggable &&
    !props.isLinkExisting &&
    props.getIconButtonMenuProps?.(props.item, props.itemHierarchicalIndex, props.documentPartWhithStatus);

  const documentName = props.item.text.replace(/\.docx$/, "");

  const [timeoutInst, setTimeoutInst] = React.useState<NodeJS.Timeout>(null);
  const [isLoadingPath, setIsLoadingPath] = React.useState<boolean>(false);
  const [pathResult, setPathResult] = React.useState<string>();
  const [tooltipPosition, setTooltipPosition] = useState<{
    positionX: number;
    positionY: number;
  } | null>(null);

  const handleTooltipCreation = (e: React.MouseEvent, item: TreeViewDataItem) => {
    setTooltipPosition((s) => ({ ...s, positionX: e.clientX, positionY: e.clientY }));
    if (!timeoutInst && !pathResult) {
      setTimeoutInst(
        setTimeout(() => {
          setIsLoadingPath(true);
          fetchDocumentPath(item.key, { params: { notShowLoading: true } })
            .then((path) =>
              setPathResult(`${DEFAULT_FOLDER_CREATION}${path}/${props.item.text.replace(/\.docx$/, "")}.docx`)
            )
            .finally(() => setIsLoadingPath(false));
        }, 1000)
      );
    }
  };

  useEffect(() => {
    if (pathResult && timeoutInst) {
      clearTimeout(timeoutInst);
      setTimeoutInst(null);
    }
  }, [pathResult, timeoutInst]);

  const handleTooltipDeletion = () => {
    clearTimeout(timeoutInst);
    setTimeoutInst(null);
    setTooltipPosition(null);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        opacity: props.item.hasReadPermissions === false ? 0.6 : 1,
        width: "100%",
        color: "black",
      }}
      onClick={(e) => {
        if (props.item.nodeType === "pagination")
          props.fetchExpandedNode(props.itemHierarchicalIndex, false, props.item.data.number + 1);
        else props.onClick?.(e);
      }}
      onMouseOver={(e) => (props.item.hasReadPermissions ? handleTooltipCreation(e, props.item) : null)}
      onMouseOut={() => (props.item.hasReadPermissions ? handleTooltipDeletion() : null)}
      /* eslint-disable-next-line no-undef */
      onKeyDown={() => console.log("")}
    >
      {tooltipPosition && (
        <Tooltip positionY={tooltipPosition.positionY} positionX={tooltipPosition.positionX}>
          {pathResult ?? documentName}
          {isLoadingPath && <Spinner size={SpinnerSize.large} id="spinner" />}
        </Tooltip>
      )}
      {props.isOutlineRibbon || props.isOpenOutlineRibbon ? (
        props.item.key !== "key" ? (
          <IconButton
            id={generateIdentifier(props.item.text, "certification_button")}
            className={
              props.documentPartWhithStatus?.length > 0
                ? iconCertification(props.item, props.documentPartWhithStatus)
                : "loading_status"
            }
            menuProps={props.item.hasReadPermissions && props.certificationOptions(props.item)}
            onRenderMenuIcon={RenderMenuIcon}
            style={{ color: "var(--color-black)", float: "right", height: "20px" }}
          />
        ) : null
      ) : (
        <span style={{ color: "blue" }} className={iconClassName(props.item)} key={`${props.item.key}_label`} />
      )}

      <span
        className={props.isSelected && props.item.hasReadPermissions ? "documentTreeBold" : "documentTreeRegular"}
        style={{
          cursor: "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
        }}
        key={`${props.item.key}_text`}
        aria-label={generateIdentifier(props.item.text, "group")}
      >
        {documentName}
      </span>
      {!props.isDraggable && multiLinked && props.item.hasReadPermissions && (
        <span className="k-icon k-i-link-vertical k-rotate-45 k-icon-sm" />
      )}
      {menuProps && (
        <IconButton
          id={generateIdentifier(props.item.text, "action_button")}
          iconProps={{ iconName: "MoreVertical" }}
          menuProps={menuProps}
          onRenderMenuIcon={RenderMenuIcon}
          style={{ color: "var(--color-black)", float: "right", height: "20px" }}
        />
      )}
    </div>
  );
};
export const DocumentPartTree: React.FunctionComponent<IDocumentPartProps> = (props) => {
  const { rootParts, getIconButtonMenuProps } = props;
  const expandIcons: boolean = props.expandIcons == undefined;
  const [state, setState] = useState<TreeState>({
    rootId: rootParts[0].key,
    treeItems: rootParts,
    selectState: [],
    page: "outline",
  });
  const refreshArrayDocumentPartWithStatus = (
    newElement: DocumentPartWithStatus[],
    array: DocumentPartWithStatus[]
  ) => {
    array = array.map((element) => {
      const matchingElement = newElement?.find((newEl) => newEl.documentPartId === element.documentPartId);
      return matchingElement ? matchingElement : element;
    });
    return array;
  };

  const [disableActionWorkflow, setDisableActionWorkflow] = useState<boolean>(false);
  let workflowStatus: WorkflowStatus;
  function changeState(item: TreeViewDataItem, action: string) {
    setDisableActionWorkflow(true);
    props.setDocumentPartWithStatus((prevState) => {
      prevState.forEach((documentPartWithStatus) => {
        if (documentPartWithStatus.documentPartId === item.key) {
          workflowStatus = documentPartWithStatus.workflowStatus;
          documentPartWithStatus.workflowStatus = null;
        }
      });
      return prevState;
    });
    fetchActionWorkflow(state.rootId, item.key, action)
      .then((items) => {
        props.setDocumentPartWithStatus((prevState) => {
          return refreshArrayDocumentPartWithStatus(items, prevState);
        });
        setDisableActionWorkflow(false);
      })
      .catch(() => restoreWorkflowStatus(item.key, workflowStatus));
  }

  function getStyle(action: string) {
    let style;
    switch (action.toLowerCase()) {
      case "approve":
        style = { fontWeight: "bold", color: "#93c33a" };
        break;
      case "reject":
        style = { fontWeight: "bold", color: "red" };
        break;
      default:
        style = { fontWeight: "bold", color: "black" };
        break;
    }
    return style;
  }
  function restoreWorkflowStatus(id: string, workflowStatus: WorkflowStatus) {
    props.setDocumentPartWithStatus((prevState) => {
      prevState.forEach((documentPartWithStatus) => {
        if (documentPartWithStatus.documentPartId === id) {
          documentPartWithStatus.workflowStatus = workflowStatus;
        }
      });
      return prevState;
    });
    setDisableActionWorkflow(false);
  }

  function certificationOptions(item: TreeViewDataItem) {
    let options: IContextualMenuItem[] = [];
    let items: IContextualMenuItem[] = [];
    let element: string[] = getWorkflowStatus(item.key, props.documentPartWithStatus)?.actions;
    let workflowStatus: WorkflowStatus = getWorkflowStatus(item.key, props.documentPartWithStatus);

    element?.forEach((action) => {
      if (!disableActionWorkflow) {
        items.push({
          key: action,
          text: formatMessage("cof.base." + action.toLowerCase()),
          onClick: () => {
            changeState(item, action);
          },
          style: getStyle(action),
        });
      }
    });
    if (items?.length > 0) {
      options.push({
        key: "menu",
        itemType: ContextualMenuItemType.Section,
        sectionProps: {
          topDivider: true,
          bottomDivider: true,
          title: getUserUpdInfo(workflowStatus),
          items: items,
        },
      });
    }
    return {
      items: options,
      directionalHintFixed: true,
    };
  }

  function getUserUpdInfo(workflowStatus: WorkflowStatus) {
    if (workflowStatus.workflowStatusCode === "50" || workflowStatus.workflowStatusCode === "30") {
      return formatMessage("cof.base.submitted.by") + " " + workflowStatus?.userUpd?.split("@")[0];
    }
  }

  useEffect(() => {
    setState({
      rootId: rootParts[0].key,
      treeItems: rootParts,
      selectState: [],
      page: "outline",
    });
  }, [rootParts]);

  const treePath = (items: TreeViewDataItem[], indexes: number[]): TreeViewDataItem[] => {
    const item = items[indexes[0]];
    if (item?.items && item.items.length > 0) return [item, ...treePath(item.items, indexes.slice(1))];
    return [item];
  };

  const onExpandChange = (event: TreeViewExpandChangeEvent) => {
    fetchExpandedNodeExternal(
      event.itemHierarchicalIndex,
      false,
      0,
      false,
      state.treeItems,
      props.elementToUpdate
    ).then((treeItems) => setState((s) => ({ ...s, treeItems })));
  };

  const fetchExpandedNode = (id: string, isReload: boolean, page: number) => {
    fetchExpandedNodeExternal(id, isReload, page, false, state.treeItems, props.elementToUpdate).then((treeItems) =>
      setState((s) => ({ ...s, treeItems }))
    );
  };

  const selectItem = (event: TreeViewItemClickEvent) => {
    if (event.item.nodeType !== "section" && event.item.nodeType !== "pagination") {
      setState((s) => ({ ...s, selectState: [event.itemHierarchicalIndex], selectedId: event.item.key }));
      if (props.onItemClick) {
        props.onItemClick({
          item: event.item,
          IdRoot: event.item.IdRoot,
          name: event.item.data.name,
          order: event.item.order,
          key: event.item.key,
          data: event.item,
          oidPath: treePath(
            state.treeItems,
            event.itemHierarchicalIndex.split("_").map((value) => Number(value))
          ).map((item) => (item ? item.key : "0")),
        });
      }
    }
  };

  const onItemClick = (event: TreeViewItemClickEvent) => {
    switch (event.syntheticEvent["detail"]) {
      case 1: // single click
        selectItem(event);
        break;
      case 2: // double click
        openDocOnDoubleClick(event.item);
        break;
    }
    event.nativeEvent.preventDefault();
  };

  const renderItemTree = (input: ItemRenderProps) => {
    return (
      <TreeItemRow
        {...input}
        isSelected={props.selectedPartId === input.item.key}
        isDraggable={state.page === "dragedrop"}
        getIconButtonMenuProps={getIconButtonMenuProps}
        fetchExpandedNode={fetchExpandedNode}
        isLinkExisting={props.isLinkExisting}
        certificationOptions={certificationOptions}
        isOutlineRibbon={props.isOutlineRibbon}
        isOpenOutlineRibbon={props.isOpenOutlineRibbon}
        azureDefaultSite={props.azureDefaultSite}
        documentPartWhithStatus={props.documentPartWithStatus}
      />
    );
  };

  function openDocOnDoubleClick(item: TreeViewDataItem) {
    if (item.hasReadPermissions !== false && item.nodeType !== "pagination") {
      props.openDocument(item.key);
    }
  }

  return (
    <>
      {props.isLibraryRibbon ? (
        <div
          style={{
            borderBottom: `0.01rem solid ${"var(--color-grey)"}`,
            width: "100%",
            height: "3px",
          }}
        />
      ) : null}
      <TreeView
        data={processTreeViewItems(state.treeItems, { select: state.selectState })}
        expandIcons={expandIcons}
        onExpandChange={onExpandChange}
        item={renderItemTree}
        onItemClick={onItemClick}
        size={"small"}
      />
    </>
  );
};

export function getWorkflowStatus(
  itemId: string,
  documentPartWithStatuses: DocumentPartWithStatus[]
): WorkflowStatus | undefined {
  return documentPartWithStatuses?.find((item) => item.documentPartId === itemId)?.workflowStatus;
}
